import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, MoreVertical, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteBrain } from "@/lib/query/hooks";
import { cn } from "@/lib/utils";
import { Brain } from "@/lib/lib/types";

interface BrainSelectorProps {
  brains: Brain[];
  activeBrainId: string;
  colonyId: string;
  onAddBrain?: () => void;
  onEditBrain?: (brainId: string) => void;
}

export function BrainSelector({ brains, activeBrainId, colonyId, onAddBrain, onEditBrain }: BrainSelectorProps) {
  const navigate = useNavigate();
  const [brainToDelete, setBrainToDelete] = useState<Brain | null>(null);
  const deleteBrainMutation = useDeleteBrain(colonyId);

  const displayBrains = useMemo(() => {
    const altMe = { id: "alt-me", name: "Alt-me", color: "#6366f1" };
    if (!Array.isArray(brains) || brains.length === 0) return [altMe];
    const rest = brains.filter((b) => b.id !== "alt-me");
    return [altMe, ...rest];
  }, [brains]);

  const handleBrainSelect = (brainId: string) => {
    if (!brainId || brainId === activeBrainId) return;
    navigate(`/chat/${colonyId}/${brainId}`);
  };

  const handleAddBrain = () => {
    if (onAddBrain) onAddBrain();
    else navigate(`/add-brain?colonyId=${colonyId}`);
  };

  const handleEditBrain = (brainId: string) => {
    if (onEditBrain) onEditBrain(brainId);
    else navigate(`/edit-brain/${colonyId}/${brainId}`);
  };

  const handleDeleteConfirm = async () => {
    if (!brainToDelete) return;
    try {
      await deleteBrainMutation.mutateAsync(brainToDelete.id);
      if (activeBrainId === brainToDelete.id) {
        navigate(`/chat/${colonyId}/alt-me`);
      }
    } catch (error) {
      // Error is handled by the mutation's onError hook
    } finally {
      setBrainToDelete(null);
    }
  };

  const isAltMe = (brainId: string) => brainId === "alt-me";

  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2 bg-ios-surface border-b border-ios-separator overflow-x-auto">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {displayBrains.map((brain) => {
            const isActive = brain.id === activeBrainId;
            const color = brain.color || "#6366f1";
            const displayName = brain.name || 'Unnamed Brain';

            return (
              <div key={brain.id} className="relative flex items-center group">
                {/* Main Brain Chip */}
                <button
                  onClick={() => handleBrainSelect(brain.id)}
                  className={cn(
                    "flex items-center gap-2 pl-2 pr-3 py-2 rounded-full whitespace-nowrap transition-all",
                    "border border-transparent",
                    isActive ? "bg-primary/10 border-primary/20" : "hover:bg-ios-surface-secondary active:scale-95"
                  )}
                >
                  <div
                    className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium shrink-0",
                      isActive && "ring-2 ring-primary/30"
                    )}
                    style={{ backgroundColor: color }}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <span className={cn(
                    "text-sm font-medium max-w-[120px] truncate",
                    isActive ? "text-primary" : "text-foreground"
                  )}>
                    {displayName}
                  </span>
                </button>

                {/* Edit/Delete Menu - Only for non-Alt-me brains */}
                {!isAltMe(brain.id) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-6 h-6 rounded-full ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVertical size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem onClick={() => handleEditBrain(brain.id)}>
                        <Edit2 size={14} className="mr-2" /> Edit Brain
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setBrainToDelete(brain)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 size={14} className="mr-2" /> Delete Brain
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Brain Button */}
        {colonyId && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleAddBrain}
            className="w-8 h-8 rounded-full bg-ios-surface-secondary hover:bg-ios-surface-tertiary shrink-0"
            aria-label="Add brain"
          >
            <Plus size={16} />
          </Button>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!brainToDelete} onOpenChange={(open) => !open && setBrainToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-destructive" /> Delete Brain
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{brainToDelete?.name || 'this brain'}</strong>? This will permanently remove the brain and all associated conversation history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBrainMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteBrainMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBrainMutation.isPending ? "Deleting..." : "Delete Brain"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


